import { motion } from "framer-motion";
import { Phone, PhoneOff, Mic, MicOff } from "lucide-react";
import type { useWebRTC } from "@/hooks/useWebRTC";

type WebRTCReturn = ReturnType<typeof useWebRTC>;

interface VoiceCallOverlayProps {
  callState: WebRTCReturn["callState"];
  isMuted: WebRTCReturn["isMuted"];
  callDuration: WebRTCReturn["callDuration"];
  onAnswer: () => void;
  onHangUp: WebRTCReturn["hangUp"];
  onToggleMute: WebRTCReturn["toggleMute"];
}

export function VoiceCallOverlay({
  callState,
  isMuted,
  callDuration,
  onAnswer,
  onHangUp,
  onToggleMute,
}: VoiceCallOverlayProps) {
  // Don't show for idle or ringing — ringing is handled by IncomingCallOverlay
  if (callState === "idle" || callState === "ringing") return null;

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const stateLabels: Record<string, string> = {
    calling: "Calling...",
    ringing: "Incoming Call",
    connected: formatTime(callDuration),
    ended: "Call Ended",
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] bg-[#0A0A0A]/95 backdrop-blur-sm flex flex-col items-center justify-center"
    >
      {/* Pulse ring animation */}
      <div className="relative mb-8">
        {callState === "ringing" && (
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-[#D4AF37]"
            animate={{ scale: [1, 2], opacity: [0.5, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        )}
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#F1D279] via-[#D4AF37] to-[#B48F1B] flex items-center justify-center">
          <Phone size={32} className="text-black" />
        </div>
      </div>

      <h2 className="text-white text-xl font-light mb-2">Voice Call</h2>
      <p className="text-gray-400 text-sm uppercase tracking-widest mb-12">
        {stateLabels[callState]}
      </p>

      <div className="flex items-center gap-6">
        {/* Mute button (only when connected) */}
        {callState === "connected" && (
          <button
            onClick={onToggleMute}
            className={`w-14 h-14 rounded-full flex items-center justify-center ${
              isMuted ? "bg-red-500/20 text-red-400" : "bg-gray-800 text-white"
            }`}
          >
            {isMuted ? <MicOff size={24} /> : <Mic size={24} />}
          </button>
        )}

        {/* Answer button (only when ringing) */}
        {callState === "ringing" && (
          <button
            onClick={onAnswer}
            className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center hover:bg-emerald-400 transition-colors"
          >
            <Phone size={28} className="text-white" />
          </button>
        )}

        {/* Hang up button (always visible except "ended") */}
        {callState !== "ended" && (
          <button
            onClick={onHangUp}
            className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center hover:bg-red-500 transition-colors"
          >
            <PhoneOff size={28} className="text-white" />
          </button>
        )}
      </div>
    </motion.div>
  );
}
